import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Target, Plus, Trash, Globe, Buildings } from "@phosphor-icons/react";

const EMPTY = { brand_name: "", persona: "", brand_voice: "", conversion_goal: "", closing_instructions: "", objections: [] };

export default function AdminClosingConfig() {
  const [tenant, setTenant] = useState("__global__");
  const [tenantInput, setTenantInput] = useState("iyeneh");
  const [data, setData] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = async (t) => {
    try {
      const { data: d } = await api.get("/ai-sales/admin/closing-config", { params: { tenant: t } });
      setData(d);
      setForm({ ...EMPTY, ...(d.config || {}), objections: (d.config?.objections || []) });
    } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(tenant); }, [tenant]);

  const save = async () => {
    setBusy(true);
    try {
      await api.post("/ai-sales/admin/closing-config", { ...form, tenant });
      toast.success("Closing config saved — the SI uses it on the next message.");
      await load(tenant);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const goals = data?.conversion_goals || [];
  const resolved = data?.resolved || {};

  const setObj = (i, k, v) => {
    const objections = [...form.objections];
    objections[i] = { ...objections[i], [k]: v };
    setForm({ ...form, objections });
  };
  const addObj = () => setForm({ ...form, objections: [...form.objections, { objection: "", response: "" }] });
  const delObj = (i) => setForm({ ...form, objections: form.objections.filter((_, j) => j !== i) });

  const inputCls = "w-full rounded-xl border border-input bg-background px-3 py-2 text-sm outline-none focus:border-primary";

  return (
    <div className="space-y-6" data-testid="admin-closing-config">
      <div className="flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/15 text-primary"><Target size={22} weight="fill" /></div>
        <div>
          <h1 className="font-display text-3xl font-semibold tracking-tight">Autonomous Closing Config</h1>
          <p className="text-sm text-muted-foreground">Give the SI its brand voice, objection playbook and conversion goal. Global defaults apply everywhere; per-tenant overrides win.</p>
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-3 rounded-2xl border border-border bg-card p-4">
        <div className="flex overflow-hidden rounded-full border border-border">
          <button data-testid="scope-global" onClick={() => setTenant("__global__")}
            className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold ${tenant === "__global__" ? "bg-primary text-primary-foreground" : "hover:bg-accent"}`}>
            <Globe size={15} weight="bold" /> Global defaults
          </button>
          <button data-testid="scope-tenant" onClick={() => setTenant(tenantInput || "iyeneh")}
            className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold ${tenant !== "__global__" ? "bg-primary text-primary-foreground" : "hover:bg-accent"}`}>
            <Buildings size={15} weight="bold" /> Per-tenant
          </button>
        </div>
        {tenant !== "__global__" && (
          <label className="block">
            <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Tenant ID (org / iyeneh)</span>
            <input data-testid="tenant-input" value={tenantInput}
              onChange={(e) => setTenantInput(e.target.value)}
              onBlur={() => setTenant(tenantInput || "iyeneh")}
              className={`mt-1 w-48 ${inputCls}`} />
          </label>
        )}
        <p className="text-xs text-muted-foreground">Editing: <span className="font-semibold text-foreground">{tenant === "__global__" ? "Global defaults" : tenant}</span></p>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <label className="block">
          <span className="text-sm font-semibold">Brand name</span>
          <input data-testid="cfg-brand" value={form.brand_name} onChange={(e) => setForm({ ...form, brand_name: e.target.value })}
            placeholder={resolved.brand_name || "iYeneh"} className={`mt-1 ${inputCls}`} />
        </label>
        <label className="block">
          <span className="text-sm font-semibold">Primary conversion goal</span>
          <select data-testid="cfg-goal" value={form.conversion_goal} onChange={(e) => setForm({ ...form, conversion_goal: e.target.value })}
            className={`mt-1 ${inputCls}`}>
            <option value="">Use default ({resolved.conversion_goal || "PURCHASE"})</option>
            {goals.map((g) => <option key={g} value={g}>{g}</option>)}
          </select>
        </label>
        <label className="block lg:col-span-2">
          <span className="text-sm font-semibold">Persona</span>
          <input data-testid="cfg-persona" value={form.persona} onChange={(e) => setForm({ ...form, persona: e.target.value })}
            placeholder={resolved.persona} className={`mt-1 ${inputCls}`} />
        </label>
        <label className="block lg:col-span-2">
          <span className="text-sm font-semibold">Brand voice</span>
          <input data-testid="cfg-voice" value={form.brand_voice} onChange={(e) => setForm({ ...form, brand_voice: e.target.value })}
            placeholder={resolved.brand_voice} className={`mt-1 ${inputCls}`} />
        </label>
        <label className="block lg:col-span-2">
          <span className="text-sm font-semibold">Closing instructions</span>
          <p className="mb-1 text-xs text-muted-foreground">Free-form rules the SI must follow when closing (e.g. always confirm the need before pitching, never discount beyond approved offers).</p>
          <textarea data-testid="cfg-instructions" rows={4} value={form.closing_instructions} onChange={(e) => setForm({ ...form, closing_instructions: e.target.value })}
            className={inputCls} />
        </label>
      </div>

      <div className="rounded-2xl border border-border bg-card p-4">
        <div className="mb-3 flex items-center justify-between">
          <div>
            <p className="text-sm font-semibold">General objection handling</p>
            <p className="text-xs text-muted-foreground">Tenant-wide objections (beyond per-product ones). The SI uses ONLY these approved responses.</p>
          </div>
          <button data-testid="cfg-add-objection" onClick={addObj} className="inline-flex items-center gap-1.5 rounded-full bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground">
            <Plus size={14} weight="bold" /> Add
          </button>
        </div>
        <div className="space-y-3" data-testid="cfg-objections">
          {form.objections.length === 0 && <p className="text-sm text-muted-foreground">No general objections yet.</p>}
          {form.objections.map((o, i) => (
            <div key={i} className="grid gap-2 rounded-xl border border-border/60 p-3 sm:grid-cols-[1fr_1.5fr_auto]">
              <input data-testid={`cfg-obj-${i}`} value={o.objection || ""} placeholder="Objection (what they say)"
                onChange={(e) => setObj(i, "objection", e.target.value)} className={inputCls} />
              <input data-testid={`cfg-resp-${i}`} value={o.response || ""} placeholder="Approved response"
                onChange={(e) => setObj(i, "response", e.target.value)} className={inputCls} />
              <button data-testid={`cfg-del-obj-${i}`} onClick={() => delObj(i)} className="inline-flex items-center justify-center rounded-xl border border-border px-3 text-red-500 hover:border-red-500">
                <Trash size={16} weight="bold" />
              </button>
            </div>
          ))}
        </div>
      </div>

      <button data-testid="cfg-save" onClick={save} disabled={busy}
        className="rounded-full bg-primary px-6 py-2.5 text-sm font-semibold text-primary-foreground disabled:opacity-60">
        {busy ? "Saving…" : `Save ${tenant === "__global__" ? "global defaults" : "tenant config"}`}
      </button>
    </div>
  );
}
